// DEPENDENCIES
import React, { useState, useEffect, useMemo, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

// CSS
import "./EmployeeViewer.css";

// API
import { getAPICall, postAPICall, deleteAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Button,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// CUSTOM COMPONENTS
import EditEmployeeDialog from "../EmployeeConsole/EditEmployeeDialog/EditEmployeeDialog";
import NewEmployeeDialog from "../EmployeeConsole/NewEmployeeDialog/NewEmployeeDialog";
import DevicesEditAddDialog from "../../HardwareInventory/Devices/DevicesEditAddDialog/DevicesEditAddDialog";

// CONSTANTS
import { notApplicable } from "../../Misc/Table/TanStackTableConstants";

const EMPLOYEE_QUERY_PARAM = "id";
const TAB_QUERY_PARAM = "tab";
const TABS = [
	{ id: "details", label: "Details" },
	{ id: "permissions", label: "Permissions" },
	{ id: "hardware", label: "Hardware" },
	{ id: "accessLogs", label: "Access Logs" },
	{ id: "rfpSubmissions", label: "RFP Submissions" },
	{ id: "rfaSubmissions", label: "RFA Submissions" },
];
const VALID_TAB_IDS = TABS.map((t) => t.id);
const DEFAULT_TAB_ID = "details";

const EmployeeViewer = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);
	const [searchParams, setSearchParams] = useSearchParams();

	// Data
	const [employees, setEmployees] = useState([]);
	const [offices, setOffices] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [jobTitles, setJobTitles] = useState([]);

	// Loading
	const [loading, setLoading] = useState(false);

	// Browser pane
	const [browserCollapsed, setBrowserCollapsed] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [officeFilter, setOfficeFilter] = useState("all");

	// Tabs — driven by URL
	const tabFromQuery = searchParams.get(TAB_QUERY_PARAM) || "";
	const activeTab = VALID_TAB_IDS.includes(tabFromQuery)
		? tabFromQuery
		: DEFAULT_TAB_ID;

	// Access logs (lazy per selected employee)
	const [accessLogs, setAccessLogs] = useState([]);
	const [accessLogsLoading, setAccessLogsLoading] = useState(false);
	const [accessLogsLoaded, setAccessLogsLoaded] = useState(false);
	const [logsFilterFrom, setLogsFilterFrom] = useState("");
	const [logsFilterTo, setLogsFilterTo] = useState("");
	const [logsFilterPage, setLogsFilterPage] = useState("");

	// Hardware (lazy per selected employee)
	const [hardware, setHardware] = useState([]);
	const [hardwareLoading, setHardwareLoading] = useState(false);
	const [hardwareLoaded, setHardwareLoaded] = useState(false);
	const [hwSortKey, setHwSortKey] = useState("j2_device_name");
	const [hwSortDir, setHwSortDir] = useState("asc");
	const [collapsedDevices, setCollapsedDevices] = useState(new Set());
	const [hwEditOpen, setHwEditOpen] = useState(false);
	const [hwCreating, setHwCreating] = useState(false);
	const [deviceToEdit, setDeviceToEdit] = useState(null);
	const [hwAssociateOpen, setHwAssociateOpen] = useState(false);
	const [hwAssociateSearch, setHwAssociateSearch] = useState("");
	const [hwAllDevices, setHwAllDevices] = useState([]);
	const [hwDeviceCategories, setHwDeviceCategories] = useState([]);
	const [hwDeviceStatuses, setHwDeviceStatuses] = useState([]);
	const [hwAllLeases, setHwAllLeases] = useState([]);
	const [hwDevicesToLeases, setHwDevicesToLeases] = useState([]);
	const [hwPurchases, setHwPurchases] = useState([]);
	const [hwDevicesToEmployees, setHwDevicesToEmployees] = useState([]);

	// Permissions (lazy per selected employee)
	const [permRoles, setPermRoles] = useState([]);
	const [permTools, setPermTools] = useState([]);
	const [permLoading, setPermLoading] = useState(false);
	const [permLoaded, setPermLoaded] = useState(false);
	const [allRoles, setAllRoles] = useState([]);
	const [roleEditing, setRoleEditing] = useState(false);
	const [roleEditValue, setRoleEditValue] = useState("");
	const [roleSaving, setRoleSaving] = useState(false);

	// RFP submissions (lazy per selected employee)
	const [rfpLogs, setRfpLogs] = useState([]);
	const [rfpLoading, setRfpLoading] = useState(false);
	const [rfpLoaded, setRfpLoaded] = useState(false);
	const [rfpFilterFrom, setRfpFilterFrom] = useState("");
	const [rfpFilterTo, setRfpFilterTo] = useState("");
	const [rfpFilterText, setRfpFilterText] = useState("");

	// RFA submissions (lazy per selected employee)
	const [rfaLogs, setRfaLogs] = useState([]);
	const [rfaLoading, setRfaLoading] = useState(false);
	const [rfaLoaded, setRfaLoaded] = useState(false);
	const [rfaFilterFrom, setRfaFilterFrom] = useState("");
	const [rfaFilterTo, setRfaFilterTo] = useState("");
	const [rfaFilterText, setRfaFilterText] = useState("");

	// Dialogs
	const [newOpen, setNewOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);

	const selectedId = searchParams.get(EMPLOYEE_QUERY_PARAM)
		? parseInt(searchParams.get(EMPLOYEE_QUERY_PARAM), 10)
		: null;

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Employee Viewer",
			employeeID: employeeID,
			notes: "Accessed the Employee Viewer page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		fetchAll();
	}, []);

	// Reset tab and cached data whenever the selected employee changes
	useEffect(() => {
		const next = new URLSearchParams(searchParams);
		next.set(TAB_QUERY_PARAM, DEFAULT_TAB_ID);
		setSearchParams(next, { replace: true });
		setAccessLogs([]);
		setAccessLogsLoaded(false);
		setLogsFilterFrom("");
		setLogsFilterTo("");
		setLogsFilterPage("");
		setHardware([]);
		setHardwareLoaded(false);
		setCollapsedDevices(new Set());
		setHwEditOpen(false);
		setHwCreating(false);
		setDeviceToEdit(null);
		setHwAssociateOpen(false);
		setHwAssociateSearch("");
		setPermRoles([]);
		setPermTools([]);
		setPermLoaded(false);
		setRoleEditing(false);
		setRfpLogs([]);
		setRfpLoaded(false);
		setRfpFilterFrom("");
		setRfpFilterTo("");
		setRfpFilterText("");
		setRfaLogs([]);
		setRfaLoaded(false);
		setRfaFilterFrom("");
		setRfaFilterTo("");
		setRfaFilterText("");
	}, [selectedId]);

	const fetchAll = async () => {
		setLoading(true);
		try {
			const [empRes, offRes, deptRes, jtRes] = await Promise.all([
				getAPICall(instance, accounts[0], "/api/employees/all"),
				getAPICall(instance, accounts[0], "/api/employees/offices/all"),
				getAPICall(instance, accounts[0], "/api/employees/departments/all"),
				getAPICall(instance, accounts[0], "/api/jobTitles/all"),
			]);
			setEmployees(empRes.data);
			setOffices(offRes.data);
			setDepartments(deptRes.data);
			setJobTitles(jtRes.data);
		} catch (err) {
			console.error("Error fetching employee data:", err);
		}
		setLoading(false);
	};

	const fetchAccessLogs = async () => {
		if (accessLogsLoaded || accessLogsLoading || !selectedId) return;
		setAccessLogsLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/access/all"
			);
			setAccessLogs(
				(res.data ?? []).filter((log) => log.employee_id === selectedId)
			);
			setAccessLogsLoaded(true);
		} catch (err) {
			console.error("Error fetching access logs:", err);
		}
		setAccessLogsLoading(false);
	};

	const fetchHardware = async (force = false) => {
		if (!force && (hardwareLoaded || hardwareLoading || !selectedId)) return;
		setHardwareLoading(true);
		try {
			const [
				devicesRes,
				dtlRes,
				leasesRes,
				serviceRes,
				categoriesRes,
				statusesRes,
				purchasesRes,
				dteRes,
			] = await Promise.all([
				getAPICall(instance, accounts[0], "/api/hardwareInventory/all"),
				getAPICall(instance, accounts[0], "/api/hardwareInventory/devicesToLeases/all"),
				getAPICall(instance, accounts[0], "/api/hardwareInventory/leases/all"),
				getAPICall(instance, accounts[0], "/api/hardwareInventory/serviceHistories/all"),
				getAPICall(instance, accounts[0], "/api/hardwareInventory/deviceCategories/all"),
				getAPICall(instance, accounts[0], "/api/hardwareInventory/deviceStatuses/all"),
				getAPICall(instance, accounts[0], "/api/hardwareInventory/purchases/all"),
				getAPICall(instance, accounts[0], "/api/hardwareInventory/devicesToEmployees/all"),
			]);

			const leaseMap = {};
			(dtlRes.data ?? []).forEach((dtl) => {
				const lease = (leasesRes.data ?? []).find((l) => l.id === dtl.lease_id);
				if (lease) {
					if (!leaseMap[dtl.device_id]) leaseMap[dtl.device_id] = [];
					leaseMap[dtl.device_id].push(lease);
				}
			});

			const serviceMap = {};
			(serviceRes.data ?? []).forEach((s) => {
				if (!serviceMap[s.device_id]) serviceMap[s.device_id] = [];
				serviceMap[s.device_id].push(s);
			});

			const filtered = (devicesRes.data ?? [])
				.filter((d) => d.employee_id === selectedId)
				.map((d) => ({
					...d,
					leases: leaseMap[d.id] ?? [],
					serviceRecords: (serviceMap[d.id] ?? []).sort(
						(a, b) => new Date(b.created) - new Date(a.created)
					),
				}));

			setHardware(filtered);
			setHwAllDevices(devicesRes.data ?? []);
			setHwDeviceCategories(categoriesRes.data ?? []);
			setHwDeviceStatuses(statusesRes.data ?? []);
			setHwAllLeases(leasesRes.data ?? []);
			setHwDevicesToLeases(dtlRes.data ?? []);
			setHwPurchases(purchasesRes.data ?? []);
			setHwDevicesToEmployees(dteRes.data ?? []);
			setHardwareLoaded(true);
		} catch (err) {
			console.error("Error fetching hardware:", err);
		}
		setHardwareLoading(false);
	};

	const fetchPermissions = async () => {
		if (permLoaded || permLoading || !selectedId) return;
		setPermLoading(true);
		try {
			const [rolesRes, toolsRes, allRolesRes] = await Promise.all([
				getAPICall(
					instance,
					accounts[0],
					`/api/permissions/roles/byEmployee?employeeID=${selectedId}`
				),
				getAPICall(
					instance,
					accounts[0],
					`/api/employees/tools?employeeID=${selectedId}`
				),
				getAPICall(instance, accounts[0], "/api/permissions/roles/all"),
			]);
			setPermRoles(rolesRes.data ?? []);
			setPermTools(toolsRes.data ?? []);
			setAllRoles(allRolesRes.data ?? []);
			setPermLoaded(true);
		} catch (err) {
			console.error("Error fetching permissions:", err);
		}
		setPermLoading(false);
	};

	const fetchRfpLogs = async () => {
		if (rfpLoaded || rfpLoading || !selectedId) return;
		setRfpLoading(true);
		try {
			const res = await getAPICall(instance, accounts[0], "/api/logs/rfp");
			setRfpLogs(
				(res.data ?? []).filter(
					(log) => log.submitted_by_employee_id === selectedId
				)
			);
			setRfpLoaded(true);
		} catch (err) {
			console.error("Error fetching RFP logs:", err);
		}
		setRfpLoading(false);
	};

	const fetchRfaLogs = async () => {
		if (rfaLoaded || rfaLoading || !selectedId) return;
		setRfaLoading(true);
		try {
			const res = await getAPICall(instance, accounts[0], "/api/logs/rfa");
			setRfaLogs(
				(res.data ?? []).filter(
					(log) => log.submitted_by_employee_id === selectedId
				)
			);
			setRfaLoaded(true);
		} catch (err) {
			console.error("Error fetching RFA logs:", err);
		}
		setRfaLoading(false);
	};

	const invalidatePermissions = () => {
		setPermLoaded(false);
	};

	const handleTabClick = (tabId) => {
		const next = new URLSearchParams(searchParams);
		next.set(TAB_QUERY_PARAM, tabId);
		setSearchParams(next, { replace: true });
		if (tabId === "hardware") fetchHardware();
		if (tabId === "permissions") fetchPermissions();
		if (tabId === "accessLogs") fetchAccessLogs();
		if (tabId === "rfpSubmissions") fetchRfpLogs();
		if (tabId === "rfaSubmissions") fetchRfaLogs();
	};

	const toggleDeviceCollapse = (deviceId) => {
		setCollapsedDevices((prev) => {
			const next = new Set(prev);
			if (next.has(deviceId)) {
				next.delete(deviceId);
			} else {
				next.add(deviceId);
			}
			return next;
		});
	};

	const handleHwSort = (key) => {
		if (hwSortKey === key) {
			setHwSortDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setHwSortKey(key);
			setHwSortDir("asc");
		}
	};

	const sortedHardware = useMemo(() => {
		return [...hardware].sort((a, b) => {
			const aVal = String(a[hwSortKey] ?? "");
			const bVal = String(b[hwSortKey] ?? "");
			return hwSortDir === "asc"
				? aVal.localeCompare(bVal)
				: bVal.localeCompare(aVal);
		});
	}, [hardware, hwSortKey, hwSortDir]);

	const filteredSortedLogs = useMemo(() => {
		const from = logsFilterFrom ? new Date(logsFilterFrom) : null;
		const to = logsFilterTo ? new Date(logsFilterTo + "T23:59:59") : null;
		return [...accessLogs]
			.filter((log) => {
				if (
					logsFilterPage &&
					!(log.page_name ?? "")
						.toLowerCase()
						.includes(logsFilterPage.toLowerCase())
				)
					return false;
				const d = log.created ? new Date(log.created) : null;
				if (from && d && d < from) return false;
				if (to && d && d > to) return false;
				return true;
			})
			.sort((a, b) => {
				const aT = new Date(a.created).getTime() || 0;
				const bT = new Date(b.created).getTime() || 0;
				return bT - aT;
			});
	}, [accessLogs, logsFilterFrom, logsFilterTo, logsFilterPage]);

	const filteredSortedRfpLogs = useMemo(() => {
		const from = rfpFilterFrom ? new Date(rfpFilterFrom) : null;
		const to = rfpFilterTo ? new Date(rfpFilterTo + "T23:59:59") : null;
		const term = rfpFilterText.toLowerCase();
		return [...rfpLogs]
			.filter((log) => {
				if (term) {
					const haystack = [
						log.property_name,
						log.service,
						log.client_first_name,
						log.client_last_name,
					]
						.filter(Boolean)
						.join(" ")
						.toLowerCase();
					if (!haystack.includes(term)) return false;
				}
				const d = log.submitted_time ? new Date(log.submitted_time) : null;
				if (from && d && d < from) return false;
				if (to && d && d > to) return false;
				return true;
			})
			.sort((a, b) => {
				const aT = new Date(a.submitted_time).getTime() || 0;
				const bT = new Date(b.submitted_time).getTime() || 0;
				return bT - aT;
			});
	}, [rfpLogs, rfpFilterFrom, rfpFilterTo, rfpFilterText]);

	const filteredSortedRfaLogs = useMemo(() => {
		const from = rfaFilterFrom ? new Date(rfaFilterFrom) : null;
		const to = rfaFilterTo ? new Date(rfaFilterTo + "T23:59:59") : null;
		const term = rfaFilterText.toLowerCase();
		return [...rfaLogs]
			.filter((log) => {
				if (term) {
					const haystack = [
						log.project_display_name,
						log.phase_name,
						log.property_name,
					]
						.filter(Boolean)
						.join(" ")
						.toLowerCase();
					if (!haystack.includes(term)) return false;
				}
				const d = log.submitted_time ? new Date(log.submitted_time) : null;
				if (from && d && d < from) return false;
				if (to && d && d > to) return false;
				return true;
			})
			.sort((a, b) => {
				const aT = new Date(a.submitted_time).getTime() || 0;
				const bT = new Date(b.submitted_time).getTime() || 0;
				return bT - aT;
			});
	}, [rfaLogs, rfaFilterFrom, rfaFilterTo, rfaFilterText]);

	// ── Role edit ──────────────────────────────────────────────────────

	const unassignedDevices = useMemo(
		() => hwAllDevices.filter((d) => !d.employee_id),
		[hwAllDevices]
	);

	const currentUserId = useMemo(() => {
		const email = accounts[0]?.username;
		return (
			employees.find(
				(e) => e.email?.toLowerCase() === email?.toLowerCase()
			)?.id ?? null
		);
	}, [employees, accounts]);

	const saveRole = async () => {
		if (roleSaving) return;
		setRoleSaving(true);
		try {
			await deleteAPICall(
				instance,
				accounts[0],
				"/api/permissions/roles/deleteByEmployee",
				{ employeeID: selectedId }
			);
			if (roleEditValue) {
				await postAPICall(
					instance,
					accounts[0],
					"/api/permissions/roles/add",
					{
						employeeID: selectedId,
						roleID: parseInt(roleEditValue, 10),
						modifiedBy: currentUserId,
					}
				);
			}
			const rolesRes = await getAPICall(
				instance,
				accounts[0],
				`/api/permissions/roles/byEmployee?employeeID=${selectedId}`
			);
			setPermRoles(rolesRes.data ?? []);
			setRoleEditing(false);
		} catch (err) {
			console.error("Error saving role:", err);
		}
		setRoleSaving(false);
	};

	// ── Lookup / format helpers ────────────────────────────────────────

	const resolveEmployeeName = (id) => {
		if (id === null || id === undefined || id === "") return notApplicable;
		const emp = employees.find((e) => e.id === id || e.id === parseInt(id, 10));
		if (!emp) return String(id);
		return `${emp.first_name} ${emp.last_name}`;
	};

	const resolveJobTitle = (id) =>
		jobTitles.find((jt) => jt.id === id)?.name ?? notApplicable;

	const resolveDepartment = (id) =>
		departments.find((d) => d.id === id)?.name ?? notApplicable;

	const formatCurrency = (val) => {
		const n = parseFloat(val);
		if (isNaN(n)) return notApplicable;
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
		}).format(n);
	};

	const formatDate = (val) => {
		if (!val) return notApplicable;
		const d = new Date(val);
		if (isNaN(d.getTime())) return notApplicable;
		return d.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const formatTimestamp = (val) => {
		if (!val) return notApplicable;
		const d = new Date(val);
		if (isNaN(d.getTime())) return notApplicable;
		return d.toLocaleString("en-US", {
			timeZone: "America/Los_Angeles",
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "2-digit",
		});
	};

	// ── Filtered employee list ─────────────────────────────────────────

	const officeOptions = useMemo(
		() =>
			[...new Set(employees.map((e) => e.office).filter(Boolean))].sort(),
		[employees]
	);

	const filteredEmployees = useMemo(() => {
		return employees.filter((e) => {
			const fullName =
				`${e.first_name ?? ""} ${e.last_name ?? ""}`.toLowerCase();
			const matchesSearch =
				!searchTerm ||
				fullName.includes(searchTerm.toLowerCase()) ||
				(e.email ?? "").toLowerCase().includes(searchTerm.toLowerCase());
			const matchesStatus =
				statusFilter === "all" ||
				(statusFilter === "active" && e.is_active) ||
				(statusFilter === "inactive" && !e.is_active);
			const matchesOffice =
				officeFilter === "all" || e.office === officeFilter;
			return matchesSearch && matchesStatus && matchesOffice;
		});
	}, [employees, searchTerm, statusFilter, officeFilter]);

	const selectedEmployee = useMemo(
		() => employees.find((e) => e.id === selectedId) ?? null,
		[employees, selectedId]
	);

	const selectEmployee = (id) => {
		setSearchParams({ [EMPLOYEE_QUERY_PARAM]: id });
	};

	// ── Browser pane ───────────────────────────────────────────────────

	const renderEmployeeCard = (emp) => {
		const isSelected = emp.id === selectedId;
		return (
			<button
				key={emp.id}
				className={`evEmployeeCard${isSelected ? " active" : ""}`}
				onClick={() => selectEmployee(emp.id)}
			>
				<div className="evEmployeeCardTopRow">
					<span className="evEmployeeCardName">
						{emp.last_name}, {emp.first_name}
					</span>
					<span
						className={`evStatusChip${
							emp.is_active ? " active" : " inactive"
						}`}
					>
						{emp.is_active ? "Active" : "Inactive"}
					</span>
				</div>
				<div className="evEmployeeCardMeta">
					<span>{resolveJobTitle(emp.job_title_id)}</span>
					<span>{emp.office ?? notApplicable}</span>
				</div>
			</button>
		);
	};

	// ── Detail pane helpers ────────────────────────────────────────────

	const renderRow = (label, value) => (
		<React.Fragment key={label}>
			<div className="evDetailKey">{label}</div>
			<div className="evDetailValue">
				{value !== null && value !== undefined && value !== ""
					? value
					: notApplicable}
			</div>
		</React.Fragment>
	);

	// ── Tab: Details ───────────────────────────────────────────────────

	const renderDetailsTab = (emp) => (
		<div className="evTabPanel">
			<div className="evDetailsTabActions">
				<Button
					variant="outlined"
					size="small"
					startIcon={<EditIcon />}
					onClick={() => setEditOpen(true)}
				>
					Edit Employee
				</Button>
			</div>
			<div className="evDetailGrid">
				<div className="evPaper evDetailCard">
					<div className="evCardHeader">
						<h3>Contact Information</h3>
					</div>
					<div className="evCardBody">
						<div className="evKeyValueGrid">
							{renderRow("Email", emp.email)}
							{renderRow("Cell Phone", emp.phone_number)}
							{renderRow("Desk Phone", emp.desk_phone)}
						</div>
					</div>
				</div>

				<div className="evPaper evDetailCard">
					<div className="evCardHeader">
						<h3>Job Details</h3>
					</div>
					<div className="evCardBody">
						<div className="evKeyValueGrid">
							{renderRow("Job Title", resolveJobTitle(emp.job_title_id))}
							{renderRow("Department", resolveDepartment(emp.department_id))}
							{renderRow("Office", emp.office)}
							{renderRow("Billable", emp.billable ? "Yes" : "No")}
							{renderRow("Billing Rate", formatCurrency(emp.billing_rate))}
							{renderRow("Employment Type", emp.employment_type === "full_time" ? "Full Time" : emp.employment_type === "part_time" ? "Part Time" : null)}
							{renderRow("Hours Per Week", emp.hours_per_week)}
						</div>
					</div>
				</div>

				<div className="evPaper evDetailCard">
					<div className="evCardHeader">
						<h3>External IDs</h3>
					</div>
					<div className="evCardBody">
						<div className="evKeyValueGrid">
							{renderRow("BQE ID", emp.bqe_id)}
							{renderRow("HubSpot ID", emp.hubspot_id)}
							{renderRow("CompanyCam ID", emp.company_cam_id)}
							{renderRow("Rippling ID (Bamboo)", emp.rippling_id)}
							{renderRow("Teams ID", emp.teams_id)}
						</div>
					</div>
				</div>

				<div className="evPaper evDetailCard">
					<div className="evCardHeader">
						<h3>Record Info</h3>
					</div>
					<div className="evCardBody">
						<div className="evKeyValueGrid">
							{renderRow("Tools ID", emp.id)}
							{renderRow("Role ID", emp.role_id)}
							{renderRow("Created", formatDate(emp.created))}
							{renderRow("Last Modified", formatDate(emp.modified))}
							{renderRow("Modified By", resolveEmployeeName(emp.modified_by))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	// ── Tab: Access Logs ───────────────────────────────────────────────

	const renderAccessLogsTab = () => (
		<div className="evTabPanel evTabPanelFill">
			<div className="evPaper evAccessLogsCard">
				<div className="evCardHeader evCardHeaderWithMeta">
					<h3>Access Logs</h3>
					{accessLogsLoaded && (
						<span className="evLogCount">
							{filteredSortedLogs.length}
							{filteredSortedLogs.length !== accessLogs.length
								? ` / ${accessLogs.length}`
								: ""}{" "}
							entries
						</span>
					)}
				</div>
				<div className="evTableFilterBar">
					<span className="evTableFilterLabel">From</span>
					<input
						type="date"
						className="evTableFilterInput"
						value={logsFilterFrom}
						onChange={(e) => setLogsFilterFrom(e.target.value)}
					/>
					<span className="evTableFilterLabel">To</span>
					<input
						type="date"
						className="evTableFilterInput"
						value={logsFilterTo}
						onChange={(e) => setLogsFilterTo(e.target.value)}
					/>
					<input
						type="text"
						className="evTableFilterInput evTableFilterTextInput"
						placeholder="Filter by page…"
						value={logsFilterPage}
						onChange={(e) => setLogsFilterPage(e.target.value)}
					/>
				</div>
				<div className="evTableScrollBody">
					{accessLogsLoading ? (
						<div className="evLoadingState">
							<CircularProgress size={28} />
						</div>
					) : accessLogsLoaded && accessLogs.length === 0 ? (
						<div className="evEmptyTableText">
							No access logs found for this employee.
						</div>
					) : accessLogsLoaded && filteredSortedLogs.length === 0 ? (
						<div className="evEmptyTableText">
							No entries match the current filters.
						</div>
					) : (
						<table className="evTable">
							<thead>
								<tr>
									<th>Date / Time (PST)</th>
									<th>Page</th>
									<th>OS</th>
									<th>Browser</th>
									<th>Notes</th>
								</tr>
							</thead>
							<tbody>
								{filteredSortedLogs.map((log) => (
									<tr key={log.id}>
										<td className="evCellNoWrap">
											{formatTimestamp(log.created)}
										</td>
										<td>{log.page_name ?? notApplicable}</td>
										<td>{log.os_info ?? notApplicable}</td>
										<td>{log.browser_info ?? notApplicable}</td>
										<td>{log.notes ?? notApplicable}</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);

	// ── Tab: Hardware ──────────────────────────────────────────────────

	const renderHardwareTab = () => {
		if (hardwareLoading) {
			return (
				<div className="evTabPanel">
					<div className="evLoadingState">
						<CircularProgress size={28} />
					</div>
				</div>
			);
		}

		if (hardwareLoaded && hardware.length === 0) {
			return (
				<div className="evTabPanel">
					<div className="evPaper">
						<div className="evEmptyTableText">
							No hardware currently assigned to this employee.
						</div>
					</div>
				</div>
			);
		}

		const filteredUnassigned = unassignedDevices.filter((d) => {
			const term = hwAssociateSearch.toLowerCase();
			if (!term) return true;
			return [d.j2_device_name, d.default_name, d.model, d.category_display_name]
				.filter(Boolean)
				.some((v) => v.toLowerCase().includes(term));
		});

		return (
			<div className="evTabPanel evTabPanelFill">
				<div className="evHwActions">
					<Button
						variant="contained"
						size="small"
						onClick={() => {
							setDeviceToEdit({ employee_id: selectedId });
							setHwCreating(true);
							setHwEditOpen(true);
						}}
					>
						New Device
					</Button>
					<Button
						variant="outlined"
						size="small"
						onClick={() => setHwAssociateOpen(true)}
					>
						Associate Existing
					</Button>
				</div>

				{/* Associate picker */}
				<Dialog
					open={hwAssociateOpen}
					onClose={() => {
						setHwAssociateOpen(false);
						setHwAssociateSearch("");
					}}
					maxWidth="sm"
					fullWidth
				>
					<DialogTitle>Associate Existing Device</DialogTitle>
					<DialogContent>
						<input
							type="text"
							className="evFilterInput"
							placeholder="Search devices…"
							value={hwAssociateSearch}
							onChange={(e) => setHwAssociateSearch(e.target.value)}
							style={{ width: "100%", marginBottom: 12 }}
						/>
						{filteredUnassigned.length === 0 ? (
							<div className="evPermEmpty">
								{unassignedDevices.length === 0
									? "No unassigned devices available."
									: "No devices match your search."}
							</div>
						) : (
							<div className="evAssociateList">
								{filteredUnassigned.map((d) => (
									<button
										key={d.id}
										type="button"
										className="evAssociateItem"
										onClick={() => {
											setHwAssociateOpen(false);
											setHwAssociateSearch("");
											setDeviceToEdit({
												...d,
												employee_id: selectedId,
											});
											setHwCreating(false);
											setHwEditOpen(true);
										}}
									>
										<span className="evAssociateItemName">
											{d.j2_device_name || d.default_name || "Unnamed"}
										</span>
										<span className="evAssociateItemMeta">
											{[d.category_display_name, d.model]
												.filter(Boolean)
												.join(" · ")}
										</span>
									</button>
								))}
							</div>
						)}
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => {
								setHwAssociateOpen(false);
								setHwAssociateSearch("");
							}}
						>
							Cancel
						</Button>
					</DialogActions>
				</Dialog>

				<div className="evHwToolbar">
					<span className="evHwCount">
						{hardware.length} device{hardware.length !== 1 ? "s" : ""}
					</span>
					<div className="evHwSortControls">
						<span className="evHwSortLabel">Sort by</span>
						<select
							className="evFilterSelect evHwSortSelect"
							value={hwSortKey}
							onChange={(e) => {
								const k = e.target.value;
								if (k !== hwSortKey) {
									setHwSortKey(k);
									setHwSortDir("asc");
								}
							}}
						>
							<option value="j2_device_name">Name</option>
							<option value="category_display_name">Category</option>
							<option value="status_display_name">Status</option>
						</select>
						<button
							type="button"
							className="evHwSortDirBtn"
							onClick={() =>
								setHwSortDir((d) => (d === "asc" ? "desc" : "asc"))
							}
						>
							{hwSortDir === "asc" ? "↑" : "↓"}
						</button>
					</div>
				</div>

				<div className="evHwCardList">
					{sortedHardware.map((device) => {
						const isCollapsed = collapsedDevices.has(device.id);
						return (
						<div key={device.id} className="evPaper evHwCard">
							<div
								className="evHwCardHeader evHwCardHeaderClickable"
								onClick={() => toggleDeviceCollapse(device.id)}
							>
								<ExpandMoreIcon
									fontSize="small"
									className={`evHwChevron${isCollapsed ? " collapsed" : ""}`}
								/>
								<span className="evHwCardName">
									{device.j2_device_name ||
										device.default_name ||
										notApplicable}
								</span>
								<span
									className={`evStatusChip${
										device.status_display_name === "Active"
											? " active"
											: " inactive"
									}`}
								>
									{device.status_display_name ?? "Unknown"}
								</span>
								<Button
									variant="outlined"
									size="small"
									startIcon={<EditIcon />}
									onClick={(e) => {
										e.stopPropagation();
										setDeviceToEdit(device);
										setHwEditOpen(true);
									}}
								>
									Edit
								</Button>
							</div>

							{!isCollapsed && <div className="evHwCardBody">
								<div className="evHwCardTopSections">
									<div className="evHwSection">
										<div className="evHwSectionTitle">Device Specs</div>
										<div className="evKeyValueGrid">
											{renderRow("Category", device.category_display_name)}
											{renderRow("Manufacturer", device.manufacturer)}
											{renderRow("Model", device.model)}
											{renderRow("OS", device.os)}
											{renderRow("RAM", device.ram)}
											{renderRow("Storage", device.storage)}
											{renderRow("Serial Number", device.serial_number)}
											{renderRow("Office", device.office_state)}
											{renderRow("Notes", device.notes)}
										</div>
									</div>

									<div className="evHwSection">
										<div className="evHwSectionTitle">Lease Info</div>
										{device.leases.length === 0 ? (
											<div className="evPermEmpty">No lease assigned.</div>
										) : (
											device.leases.map((lease, idx) => (
												<div
													key={lease.id ?? idx}
													className="evHwLeaseBlock"
												>
													<div className="evKeyValueGrid">
														{renderRow(
															"Lease Number",
															lease.lease_number
														)}
														{renderRow(
															"Origin Date",
															formatDate(lease.origin_date)
														)}
														{renderRow(
															"Term Date",
															formatDate(lease.term_date)
														)}
														{renderRow(
															"Lease Length",
															lease.lease_length
														)}
													</div>
												</div>
											))
										)}
									</div>
								</div>

								<div className="evHwServiceSection">
									<div className="evHwSectionTitle">Service History</div>
									{device.serviceRecords.length === 0 ? (
										<div className="evPermEmpty">No service records.</div>
									) : (
										<table className="evTable">
											<thead>
												<tr>
													<th>Date</th>
													<th>Incident Type</th>
													<th>Ticket #</th>
													<th>Notes</th>
												</tr>
											</thead>
											<tbody>
												{device.serviceRecords.map((r) => (
													<tr key={r.id}>
														<td className="evCellNoWrap">
															{formatDate(r.created)}
														</td>
														<td>
															{r.incident_type_display_name ??
																notApplicable}
														</td>
														<td>
															{r.ticket_number ?? notApplicable}
														</td>
														<td>{r.notes ?? notApplicable}</td>
													</tr>
												))}
											</tbody>
										</table>
									)}
								</div>
							</div>}
						</div>
						);
					})}
				</div>
			</div>
		);
	};

	// ── Tab: Permissions ───────────────────────────────────────────────

	const renderPermissionsTab = () => {
		if (permLoading) {
			return (
				<div className="evTabPanel">
					<div className="evLoadingState">
						<CircularProgress size={28} />
					</div>
				</div>
			);
		}

		const adminTools = permTools.filter((t) => t.admin_access);
		const regularTools = permTools.filter((t) => !t.admin_access);

		const role = permRoles[0] ?? null;

		return (
			<div className="evTabPanel">
				<div className="evPaper evDetailCard">
					<div className="evCardHeader evCardHeaderWithMeta">
						<h3>Role</h3>
						{permLoaded && !roleEditing && (
							<button
								type="button"
								className="evRoleEditBtn"
								onClick={() => {
									setRoleEditValue(role ? String(role.id) : "");
									setRoleEditing(true);
								}}
							>
								<EditIcon fontSize="inherit" />
								Edit
							</button>
						)}
					</div>
					<div className="evCardBody">
						{roleEditing ? (
							<div className="evRoleEditRow">
								<select
									className="evFilterSelect evRoleSelect"
									value={roleEditValue}
									onChange={(e) => setRoleEditValue(e.target.value)}
									disabled={roleSaving}
								>
									<option value="">— No role —</option>
									{allRoles.map((r) => (
										<option key={r.id} value={String(r.id)}>
											{r.name}
										</option>
									))}
								</select>
								<Button
									variant="contained"
									size="small"
									onClick={saveRole}
									disabled={roleSaving}
								>
									{roleSaving ? (
										<CircularProgress size={14} color="inherit" />
									) : (
										"Save"
									)}
								</Button>
								<Button
									variant="outlined"
									size="small"
									onClick={() => setRoleEditing(false)}
									disabled={roleSaving}
								>
									Cancel
								</Button>
							</div>
						) : permLoaded && !role ? (
							<div className="evPermEmpty">No role assigned.</div>
						) : role ? (
							<div className="evPermRoleItem">
								<span className="evPermRoleName">{role.name}</span>
							</div>
						) : null}
					</div>
				</div>

				<div className="evPaper evDetailCard">
					<div className="evCardHeader evCardHeaderWithMeta">
						<h3>Tool Access</h3>
						{permLoaded && (
							<span className="evLogCount">{permTools.length} tools</span>
						)}
					</div>
					<div className="evCardBody">
						{permLoaded && permTools.length === 0 ? (
							<div className="evPermEmpty">No tools assigned.</div>
						) : (
							<div className="evPermToolList">
								{regularTools.map((tool) => (
									<div key={tool.id} className="evPermToolItem">
										<span className="evPermToolName">{tool.name}</span>
									</div>
								))}
								{adminTools.map((tool) => (
									<div key={tool.id} className="evPermToolItem">
										<span className="evPermToolName">{tool.name}</span>
										<span className="evPermAdminChip">Admin</span>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	// ── Tab: RFP Submissions ───────────────────────────────────────────

	const renderRfpTab = () => (
		<div className="evTabPanel evTabPanelFill">
			<div className="evPaper evAccessLogsCard">
				<div className="evCardHeader evCardHeaderWithMeta">
					<h3>RFP Submissions</h3>
					{rfpLoaded && (
						<span className="evLogCount">
							{filteredSortedRfpLogs.length}
							{filteredSortedRfpLogs.length !== rfpLogs.length
								? ` / ${rfpLogs.length}`
								: ""}{" "}
							entries
						</span>
					)}
				</div>
				<div className="evTableFilterBar">
					<span className="evTableFilterLabel">From</span>
					<input
						type="date"
						className="evTableFilterInput"
						value={rfpFilterFrom}
						onChange={(e) => setRfpFilterFrom(e.target.value)}
					/>
					<span className="evTableFilterLabel">To</span>
					<input
						type="date"
						className="evTableFilterInput"
						value={rfpFilterTo}
						onChange={(e) => setRfpFilterTo(e.target.value)}
					/>
					<input
						type="text"
						className="evTableFilterInput evTableFilterTextInput"
						placeholder="Filter by client, property, or service…"
						value={rfpFilterText}
						onChange={(e) => setRfpFilterText(e.target.value)}
					/>
				</div>
				<div className="evTableScrollBody">
					{rfpLoading ? (
						<div className="evLoadingState">
							<CircularProgress size={28} />
						</div>
					) : rfpLoaded && rfpLogs.length === 0 ? (
						<div className="evEmptyTableText">
							No RFP submissions found for this employee.
						</div>
					) : rfpLoaded && filteredSortedRfpLogs.length === 0 ? (
						<div className="evEmptyTableText">
							No entries match the current filters.
						</div>
					) : (
						<table className="evTable">
							<thead>
								<tr>
									<th>Submitted</th>
									<th>Client</th>
									<th>Property</th>
									<th>Service</th>
									<th>Due Date</th>
									<th>Error</th>
								</tr>
							</thead>
							<tbody>
								{filteredSortedRfpLogs.map((log) => (
									<tr key={log.id}>
										<td className="evCellNoWrap">
											{formatTimestamp(log.submitted_time)}
										</td>
										<td>
											{log.client_first_name || log.client_last_name
												? `${log.client_first_name ?? ""} ${log.client_last_name ?? ""}`.trim()
												: notApplicable}
										</td>
										<td>{log.property_name ?? notApplicable}</td>
										<td>{log.service ?? notApplicable}</td>
										<td className="evCellNoWrap">
											{log.proposal_due_date
												? formatDate(log.proposal_due_date)
												: notApplicable}
										</td>
										<td>{log.error_message ?? notApplicable}</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);

	// ── Tab: RFA Submissions ───────────────────────────────────────────

	const renderRfaTab = () => (
		<div className="evTabPanel evTabPanelFill">
			<div className="evPaper evAccessLogsCard">
				<div className="evCardHeader evCardHeaderWithMeta">
					<h3>RFA Submissions</h3>
					{rfaLoaded && (
						<span className="evLogCount">
							{filteredSortedRfaLogs.length}
							{filteredSortedRfaLogs.length !== rfaLogs.length
								? ` / ${rfaLogs.length}`
								: ""}{" "}
							entries
						</span>
					)}
				</div>
				<div className="evTableFilterBar">
					<span className="evTableFilterLabel">From</span>
					<input
						type="date"
						className="evTableFilterInput"
						value={rfaFilterFrom}
						onChange={(e) => setRfaFilterFrom(e.target.value)}
					/>
					<span className="evTableFilterLabel">To</span>
					<input
						type="date"
						className="evTableFilterInput"
						value={rfaFilterTo}
						onChange={(e) => setRfaFilterTo(e.target.value)}
					/>
					<input
						type="text"
						className="evTableFilterInput evTableFilterTextInput"
						placeholder="Filter by project, phase, or property…"
						value={rfaFilterText}
						onChange={(e) => setRfaFilterText(e.target.value)}
					/>
				</div>
				<div className="evTableScrollBody">
					{rfaLoading ? (
						<div className="evLoadingState">
							<CircularProgress size={28} />
						</div>
					) : rfaLoaded && rfaLogs.length === 0 ? (
						<div className="evEmptyTableText">
							No RFA submissions found for this employee.
						</div>
					) : rfaLoaded && filteredSortedRfaLogs.length === 0 ? (
						<div className="evEmptyTableText">
							No entries match the current filters.
						</div>
					) : (
						<table className="evTable">
							<thead>
								<tr>
									<th>Submitted</th>
									<th>Project</th>
									<th>Phase</th>
									<th>Property</th>
									<th>Recipient</th>
									<th>Error</th>
								</tr>
							</thead>
							<tbody>
								{filteredSortedRfaLogs.map((log) => (
									<tr key={log.id}>
										<td className="evCellNoWrap">
											{formatTimestamp(log.submitted_time)}
										</td>
										<td>{log.project_display_name ?? notApplicable}</td>
										<td>{log.phase_name ?? notApplicable}</td>
										<td>{log.property_name ?? notApplicable}</td>
										<td>
											{log.recipient_first_name || log.recipient_last_name
												? `${log.recipient_first_name ?? ""} ${log.recipient_last_name ?? ""}`.trim()
												: notApplicable}
										</td>
										<td>{log.error_message ?? notApplicable}</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);

	// ── Full detail pane ───────────────────────────────────────────────

	const renderDetail = () => {
		if (!selectedEmployee) {
			return (
				<div className="evEmptyState">
					<div className="evEmptyStateText">
						Select an employee from the list to view their details.
					</div>
				</div>
			);
		}

		const emp = selectedEmployee;

		return (
			<div className="evDetailContent">
				{/* Hero */}
				<div className="evPaper evDetailHero">
					<div className="evDetailHeroInner">
						<div className="evDetailHeroLeft">
							<div className="evDetailEyebrow">Employee</div>
							<h1 className="evDetailTitle">
								{emp.first_name} {emp.last_name}
								{emp.initials && (
									<span className="evDetailInitials">
										{" "}
										({emp.initials})
									</span>
								)}
							</h1>
							<div className="evDetailSubtitle">
								{resolveJobTitle(emp.job_title_id)}
								{emp.office && (
									<>
										<span className="evDetailSep">{"·"}</span>
										{emp.office}
									</>
								)}
							</div>
						</div>
						<div className="evDetailHeroActions">
							<span
								className={`evStatusChip large${
									emp.is_active ? " active" : " inactive"
								}`}
							>
								{emp.is_active ? "Active" : "Inactive"}
							</span>
						</div>
					</div>

					{/* Tab bar inside hero card */}
					<div className="evTabs">
						{TABS.map((tab) => (
							<button
								key={tab.id}
								type="button"
								className={`evTab${activeTab === tab.id ? " active" : ""}`}
								onClick={() => handleTabClick(tab.id)}
							>
								{tab.label}
							</button>
						))}
					</div>
				</div>

				{/* Tab content — scrollable region below the hero */}
				<div className="evTabScrollWrapper">
					{activeTab === "details" && renderDetailsTab(emp)}
					{activeTab === "permissions" && renderPermissionsTab()}
					{activeTab === "hardware" && renderHardwareTab()}
					{activeTab === "accessLogs" && renderAccessLogsTab()}
					{activeTab === "rfpSubmissions" && renderRfpTab()}
					{activeTab === "rfaSubmissions" && renderRfaTab()}
				</div>
			</div>
		);
	};

	// ── Root ───────────────────────────────────────────────────────────

	return (
		<div className="EmployeeViewer">
			<div className="evPage">
				<div
					className={`evWorkspace${
						browserCollapsed ? " browserCollapsed" : ""
					}`}
				>
					{/* LEFT: Employee browser */}
					<div className="evBrowserPane evPaper">
						<div className="evBrowserHeader">
							{!browserCollapsed && (
								<div className="evBrowserHeaderTitle">
									<h3>Employees</h3>
									<Button
										variant="contained"
										size="small"
										onClick={() => setNewOpen(true)}
										disabled={loading}
									>
										New
									</Button>
								</div>
							)}
							<button
								type="button"
								className="evBrowserToggle"
								aria-label={
									browserCollapsed
										? "Expand employee list"
										: "Collapse employee list"
								}
								onClick={() => setBrowserCollapsed((c) => !c)}
							>
								{browserCollapsed ? (
									<ChevronRightIcon fontSize="inherit" />
								) : (
									<ChevronLeftIcon fontSize="inherit" />
								)}
							</button>
						</div>

						<div
							className={`evBrowserBody${
								browserCollapsed ? " collapsed" : ""
							}`}
						>
							<div className="evBrowserFilters">
								<input
									type="text"
									className="evFilterInput"
									placeholder="Search by name or email"
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
								/>
								<select
									className="evFilterSelect"
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}
								>
									<option value="all">All Employees</option>
									<option value="active">Active Only</option>
									<option value="inactive">Inactive Only</option>
								</select>
								<select
									className="evFilterSelect"
									value={officeFilter}
									onChange={(e) => setOfficeFilter(e.target.value)}
								>
									<option value="all">All Offices</option>
									{officeOptions.map((office) => (
										<option key={office} value={office}>
											{office}
										</option>
									))}
								</select>
							</div>

							<div className="evEmployeeList">
								{loading ? (
									[...Array(6)].map((_, i) => (
										<div key={i} className="evEmployeeCardSkeleton" />
									))
								) : filteredEmployees.length === 0 ? (
									<div className="evBrowserEmpty">No employees found.</div>
								) : (
									filteredEmployees.map(renderEmployeeCard)
								)}
							</div>
						</div>
					</div>

					{/* RIGHT: Detail */}
					<div className="evMainPane">
						{loading ? (
							<div className="evLoadingState">
								<CircularProgress />
							</div>
						) : (
							renderDetail()
						)}
					</div>
				</div>
			</div>

			<NewEmployeeDialog
				open={newOpen}
				newEmployeeDialogClosed={() => setNewOpen(false)}
				fetchEmployees={fetchAll}
				offices={offices}
				departments={departments}
				jobTitles={jobTitles}
			/>

			<DevicesEditAddDialog
				open={hwEditOpen}
				editing={!hwCreating}
				recordToEdit={deviceToEdit ?? {}}
				onClose={() => {
					setHwEditOpen(false);
					fetchHardware(true);
				}}
				onCloseNoRefresh={() => setHwEditOpen(false)}
				devices={hwAllDevices}
				deviceCategories={hwDeviceCategories}
				deviceStatuses={hwDeviceStatuses}
				offices={offices}
				employees={employees}
				leases={hwAllLeases}
				devicesToLeases={hwDevicesToLeases}
				purchases={hwPurchases}
				devicesToEmployees={hwDevicesToEmployees}
			/>

			<EditEmployeeDialog
				open={editOpen}
				recordToEdit={selectedEmployee ?? {}}
				editEmployeeDialogClosed={() => setEditOpen(false)}
				fetchEmployees={fetchAll}
				offices={offices}
				departments={departments}
				jobTitles={jobTitles}
				onRoleChanged={invalidatePermissions}
			/>
		</div>
	);
};

export default EmployeeViewer;
